import { MouseEvent, forwardRef, useContext } from "react";
import { Link, Navigate } from "react-router-dom";

import { UserCompanyLink } from "@/model/UserCompanyConfiguration/UserCompanyConfiguration";
import { UserCompanyConfigurationContext } from "@/model/UserCompanyConfiguration/UserCompanyConfigurationContext";

export interface ISATOSLinkProps {
	className?: string;
	params?: Array<string | number | undefined>;
	redirect?: boolean;
	search?: string;
	serverSideRendering?: boolean;
	stopPropagation?: boolean;
	target?: "_blank" | "";
	to: string;
	onClick?: () => void;
	onClickAsync?: () => Promise<any>;
	children?: React.ReactNode;
}

export const SATOSLink = forwardRef<
	HTMLAnchorElement | HTMLSpanElement,
	ISATOSLinkProps
>((props, ref) => {
	const userCompanyConfiguration = useContext(UserCompanyConfigurationContext);

	const linkRules = userCompanyConfiguration?.Links;

	const handleOnClick = async (event: MouseEvent) => {
		if (props.stopPropagation) {
			event.stopPropagation();
		}

		if (props.onClick) {
			props.onClick();
		}

		if (props.onClickAsync) {
			await props.onClickAsync();
		}
	};

	// TODO: Abstract to helper file.
	const formatUrl = (
		linkRule: UserCompanyLink,
		params: Array<string | number | undefined> | undefined,
	) => {
		if (!params || params.length === 0) {
			return linkRule.Url;
		}

		return linkRule?.Url
			? linkRule.Url.replace(
					/{(\d+)}/g,
					(_, index) => params[index]?.toString() || "",
				)
			: "";
	};

	const getLinkElement = (options: {
		to: string;
		search?: string;
		children?: any;
		serverSideRendering?: boolean;
		target?: string;
	}) => {
		if (
			options.to.startsWith(
				`${window.location.protocol}//${window.location.host}`,
			)
		) {
			return (
				<Link
					className={props.className}
					onClick={(event: MouseEvent) => {
						handleOnClick(event);
					}}
					to={{ pathname: options.to, search: options.search }}
					target={options.target}
					replace
				>
					{options?.children}
				</Link>
			);
		}

		return (
			<a
				className={props.className}
				onClick={(event: MouseEvent) => {
					handleOnClick(event);
				}}
				href={options.to}
				target={options.target}
			>
				{options?.children}
			</a>
		);
	};

	if (!linkRules || linkRules?.Links) {
		return getLinkElement(props);
	}

	const linkRule = linkRules[props.to];

	if (linkRule) {
		const url = linkRule ? formatUrl(linkRule, props.params) : props.to;

		if (props.redirect !== undefined) {
			if (props.redirect && linkRule.Local) {
				return <Navigate to={{ pathname: `/${url}`, search: props.search }} />;
			} else if (props.redirect) {
				window.location.href = `${linkRule.BaseUrl}${encodeURI(url)}`;
			} else {
				return null;
			}
		}

		if (linkRule.Local && props.serverSideRendering) {
			return (
				<a
					className={props.className}
					onClick={(event: MouseEvent) => {
						handleOnClick(event);
					}}
					href={`${window.location.protocol}//${
						window.location.host
					}/${encodeURI(url)}`}
					target={props.target}
				>
					{props.children}
				</a>
			);
		}

		if (linkRule.Local) {
			return (
				<Link
					className={props.className}
					onClick={(event: MouseEvent) => {
						handleOnClick(event);
					}}
					to={{ pathname: `/${url}`, search: props.search }}
					target={props.target}
				>
					{props.children}
				</Link>
			);
		}

		if (window && window.location.protocol.startsWith("http")) {
			return (
				<a
					className={props.className}
					onClick={(event: MouseEvent) => {
						handleOnClick(event);
					}}
					href={`${linkRule.BaseUrl}${url}`}
					target={props.target}
				>
					{props.children}
				</a>
			);
		}
		return (
			<a
				className={props.className}
				onClick={(event: MouseEvent) => {
					handleOnClick(event);
				}}
				href={url}
				target={props.target}
			>
				{props.children}
			</a>
		);
	}

	return getLinkElement(props);
});
